/**
 * `GanttCanvas` — the modern gantt surface (redesign R1).
 *
 * A split **task-list panel** (names + owner) and a scrolling **timeline** with a two-tier date
 * axis (month band over day/week ticks), weekend shading, a today line, rounded status bars with a
 * percent-complete fill, milestone diamonds, and subtle **finish-to-start** dependency elbows. A
 * toolbar offers zoom (Day/Week/Month), Today, Fit, and a Dependencies toggle. Geometry comes from
 * the pure `computeGanttView`.
 *
 * Direct manipulation (when `onCommit` is given — every source-changing edit re-serializes the spec
 * back to the fence):
 *   • **click a bar/milestone** to open the right-docked **metadata inspector** — edit title, dates,
 *     status, owner, milestone, a free-text description, and arbitrary key/value fields; the
 *     description + fields ride in a `%% @aletheia:gantt` sidecar so MCP and chat share them;
 *   • **drag a bar** to reschedule, **drag either end** to resize (snap to day, live preview);
 *   • **double-click a row name** to rename it inline (task rows and section headers);
 *   • **✕** on a row deletes it (a section only once it is empty);
 *   • **collapse a section** via its header chevron to tame a dense multi-section chart.
 * View-only affordances are persisted to `localStorage` rather than the fence, since mermaid gantt
 * has nowhere to put them: panel-divider width, per-row height, and section collapse state.
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ganttTasks, serializeGantt, type GanttSpec, type GanttStatus } from '../gantt';
import {
  computeGanttView,
  clampPanelWidth,
  clampRowHeight,
  deleteSection,
  deleteTask,
  renameSection,
  renameTask,
  rescheduleTask,
  resizeTask,
  resizeTaskStart,
  updateTask,
  GANTT_PANEL_DEFAULT,
  type GanttTaskPatch,
  type GanttZoom,
} from '../ganttLayout';
import { GanttInspector } from './GanttInspector';
import './gantt-canvas.css';

const STATUS_FILL: Record<GanttStatus, string> = {
  normal: '#e2e8f0',
  active: '#dbeafe',
  done: '#dcfce7',
  crit: '#fee2e2',
};
const STATUS_STROKE: Record<GanttStatus, string> = {
  normal: '#94a3b8',
  active: '#2563eb',
  done: '#16a34a',
  crit: '#dc2626',
};
const MILESTONE_FILL = '#fef3c7';
const MILESTONE_STROKE = '#d97706';
/** Width of the grab strip at each end of a bar (the resize handles). */
const RESIZE_ZONE = 7;
const ZOOMS: GanttZoom[] = ['day', 'week', 'month'];
/** Legend labels for the status palette (deck-ready key — a static slide has no hover tooltips). */
const STATUS_LABEL: Record<GanttStatus, string> = {
  done: 'Complete',
  active: 'In progress',
  crit: 'Critical path',
  normal: 'Planned',
};
const LEGEND_ORDER: GanttStatus[] = ['done', 'active', 'crit', 'normal'];

// ── persisted view preferences ───────────────────────────────────────────────
// Panel width and row heights are *view* state, not chart data: mermaid gantt has no syntax for
// them and writing them into the fence would fight the MCP/Python round-trip. They live in
// localStorage instead — panel width globally, row heights per chart (keyed by title).

const PANEL_WIDTH_KEY = 'aletheia.ganttPanelWidth';
const ROW_HEIGHTS_PREFIX = 'aletheia.ganttRowHeights.';

function loadPanelWidth(): number {
  try {
    const raw = localStorage.getItem(PANEL_WIDTH_KEY);
    const n = raw === null ? NaN : Number.parseInt(raw, 10);
    return Number.isNaN(n) ? GANTT_PANEL_DEFAULT : clampPanelWidth(n);
  } catch {
    return GANTT_PANEL_DEFAULT;
  }
}

function savePanelWidth(px: number): void {
  try {
    localStorage.setItem(PANEL_WIDTH_KEY, String(Math.round(px)));
  } catch {
    /* unavailable / quota — a view pref is never load-bearing */
  }
}

function loadRowHeights(title: string): Record<string, number> {
  if (!title) return {};
  try {
    const raw = localStorage.getItem(ROW_HEIGHTS_PREFIX + title);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [id, h] of Object.entries(parsed as Record<string, unknown>)) {
      if (typeof h === 'number' && Number.isFinite(h)) out[id] = clampRowHeight(h);
    }
    return out;
  } catch {
    return {};
  }
}

function saveRowHeights(title: string, heights: Record<string, number>): void {
  if (!title) return;
  try {
    localStorage.setItem(ROW_HEIGHTS_PREFIX + title, JSON.stringify(heights));
  } catch {
    /* unavailable / quota — heights fall back to the default row */
  }
}

// Section collapse is also view state (no mermaid gantt syntax for it), so per-chart localStorage —
// keyed by section index, which is stable as long as sections aren't reordered.
const COLLAPSED_PREFIX = 'aletheia.ganttCollapsed.';

function loadCollapsed(title: string): Set<number> {
  if (!title) return new Set();
  try {
    const raw = localStorage.getItem(COLLAPSED_PREFIX + title);
    const parsed: unknown = raw === null ? null : JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((n): n is number => typeof n === 'number' && Number.isInteger(n)));
  } catch {
    return new Set();
  }
}

function saveCollapsed(title: string, collapsed: ReadonlySet<number>): void {
  if (!title) return;
  try {
    localStorage.setItem(COLLAPSED_PREFIX + title, JSON.stringify([...collapsed]));
  } catch {
    /* unavailable / quota — collapse state is never load-bearing */
  }
}

export interface GanttCanvasProps {
  spec: GanttSpec;
  /** When present, the chart is editable: called with the new mermaid gantt source after an edit. */
  onCommit?: (nextSource: string) => void;
  /** Presentation mode: hide the interactive toolbar and paint a fixed light "slide" surface. */
  presenting?: boolean;
}

type DragMode = 'move' | 'resize-end' | 'resize-start';

interface DragState {
  id: string;
  mode: DragMode;
  startClientX: number;
  deltaDays: number;
}

/** Which row label is open in the inline rename editor. */
type EditTarget = { kind: 'task'; id: string } | { kind: 'section'; index: number };

export function GanttCanvas({ spec, onCommit, presenting = false }: GanttCanvasProps): JSX.Element {
  const [zoom, setZoom] = useState<GanttZoom>('week');
  const [showDeps, setShowDeps] = useState(true);
  const [drag, setDrag] = useState<DragState | null>(null);
  const [editing, setEditing] = useState<EditTarget | null>(null);
  const [draft, setDraft] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const editable = typeof onCommit === 'function';

  // ── selection (drives the metadata inspector) ──────────────────────────────
  const [selectedId, setSelectedId] = useState<string | null>(null);

  // ── persisted view prefs ───────────────────────────────────────────────────
  const [panelWidth, setPanelWidth] = useState(loadPanelWidth);
  const [rowHeights, setRowHeights] = useState<Record<string, number>>(() => loadRowHeights(spec.title));
  const [collapsed, setCollapsed] = useState<Set<number>>(() => loadCollapsed(spec.title));
  // A different chart (title changed / another block mounted this state) gets its own view prefs.
  const titleRef = useRef(spec.title);
  useEffect(() => {
    if (titleRef.current !== spec.title) {
      titleRef.current = spec.title;
      setRowHeights(loadRowHeights(spec.title));
      setCollapsed(loadCollapsed(spec.title));
      setSelectedId(null);
    }
  }, [spec.title]);

  // Available timeline space, so the chart fills the container (esp. fullscreen) instead of leaving
  // a blank void to the right/below. Measured from the scroll viewport; re-measured on resize.
  const [avail, setAvail] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const measure = (): void =>
      setAvail((prev) => {
        const w = el.clientWidth;
        const h = el.clientHeight;
        return Math.abs(prev.w - w) > 1 || Math.abs(prev.h - h) > 1 ? { w, h } : prev;
      });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const view = useMemo(
    () =>
      computeGanttView(spec, {
        zoom,
        today: spec.todayMarker ? new Date() : undefined,
        minWidth: avail.w,
        minHeight: avail.h,
        labelWidth: panelWidth,
        rowHeights,
        collapsed,
      }),
    [spec, zoom, avail.w, avail.h, panelWidth, rowHeights, collapsed],
  );
  const { pxPerDay, labelWidth, timelineWidth, axisHeight, canvasHeight } = view;

  // The task backing the open inspector (null if nothing selected or the task was deleted).
  const selectedTask = useMemo(
    () => (selectedId ? (ganttTasks(spec).find((t) => t.id === selectedId) ?? null) : null),
    [spec, selectedId],
  );

  // Which status swatches / milestone glyph the legend needs (only what's actually used).
  const legend = useMemo(() => {
    const statuses = new Set<GanttStatus>();
    let hasMilestone = false;
    for (const s of spec.sections)
      for (const t of s.tasks) {
        if (t.milestone) hasMilestone = true;
        else statuses.add(t.status);
      }
    return { statuses, hasMilestone };
  }, [spec]);

  const moveableIds = useMemo(() => {
    const set = new Set<string>();
    for (const s of spec.sections) for (const t of s.tasks) if (t.start) set.add(t.id);
    return set;
  }, [spec]);

  const commit = useCallback(
    (next: GanttSpec) => {
      if (onCommit && next !== spec) onCommit(serializeGantt(next));
    },
    [onCommit, spec],
  );

  // ── bar drag: move / resize from either end ────────────────────────────────
  const onBarDown = useCallback(
    (e: React.PointerEvent, id: string, mode: DragMode) => {
      if (!editable) return;
      if (mode !== 'resize-end' && !moveableIds.has(id)) return; // after-anchored: start is derived
      try {
        (e.currentTarget as Element).setPointerCapture(e.pointerId);
      } catch {
        /* no-op */
      }
      e.stopPropagation();
      setDrag({ id, mode, startClientX: e.clientX, deltaDays: 0 });
    },
    [editable, moveableIds],
  );
  const onPointerMove = useCallback(
    (e: React.PointerEvent) => {
      if (!drag) return;
      const delta = Math.round((e.clientX - drag.startClientX) / pxPerDay);
      if (delta !== drag.deltaDays) setDrag({ ...drag, deltaDays: delta });
    },
    [drag, pxPerDay],
  );
  const onPointerUp = useCallback(() => {
    if (drag && drag.deltaDays !== 0) {
      if (drag.mode === 'move') {
        commit(rescheduleTask(spec, drag.id, drag.deltaDays));
      } else if (drag.mode === 'resize-start') {
        commit(resizeTaskStart(spec, drag.id, drag.deltaDays));
      } else {
        const bar = view.rows.find((r) => r.id === drag.id)?.bar;
        if (bar) commit(resizeTask(spec, drag.id, Math.round(bar.width / pxPerDay) + drag.deltaDays));
      }
    } else if (drag && drag.deltaDays === 0) {
      // A press with no drag is a click → select the item and open its metadata inspector.
      setSelectedId(drag.id);
    }
    setDrag(null);
  }, [drag, commit, spec, view.rows, pxPerDay]);

  // ── metadata inspector ──────────────────────────────────────────────────────
  const patchSelected = useCallback(
    (patch: GanttTaskPatch) => {
      if (selectedId) commit(updateTask(spec, selectedId, patch));
    },
    [commit, spec, selectedId],
  );

  // ── collapse ────────────────────────────────────────────────────────────────
  const toggleCollapsed = useCallback(
    (index: number) => {
      setCollapsed((prev) => {
        const next = new Set(prev);
        if (next.has(index)) next.delete(index);
        else next.add(index);
        saveCollapsed(spec.title, next);
        return next;
      });
    },
    [spec.title],
  );

  // ── panel divider drag (view pref) ─────────────────────────────────────────
  const [panelDrag, setPanelDrag] = useState<{ startX: number; startW: number } | null>(null);
  useEffect(() => {
    if (!panelDrag) return;
    const onMove = (e: PointerEvent): void =>
      setPanelWidth(clampPanelWidth(panelDrag.startW + (e.clientX - panelDrag.startX)));
    const onUp = (): void => setPanelDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [panelDrag]);
  useEffect(() => {
    if (!panelDrag) savePanelWidth(panelWidth);
  }, [panelDrag, panelWidth]);

  // ── row-height drag (view pref) ────────────────────────────────────────────
  const [rowDrag, setRowDrag] = useState<{ id: string; startY: number; startH: number } | null>(null);
  useEffect(() => {
    if (!rowDrag) return;
    const onMove = (e: PointerEvent): void =>
      setRowHeights((prev) => ({
        ...prev,
        [rowDrag.id]: clampRowHeight(rowDrag.startH + (e.clientY - rowDrag.startY)),
      }));
    const onUp = (): void => setRowDrag(null);
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
  }, [rowDrag]);
  useEffect(() => {
    if (!rowDrag) saveRowHeights(spec.title, rowHeights);
  }, [rowDrag, rowHeights, spec.title]);

  // ── inline rename ──────────────────────────────────────────────────────────
  const startEdit = useCallback(
    (target: EditTarget, current: string) => {
      if (!editable) return;
      setEditing(target);
      setDraft(current);
    },
    [editable],
  );
  const commitEdit = useCallback(() => {
    if (!editing) return;
    commit(
      editing.kind === 'task'
        ? renameTask(spec, editing.id, draft)
        : renameSection(spec, editing.index, draft),
    );
    setEditing(null);
  }, [editing, draft, commit, spec]);
  const onEditKey = useCallback(
    (e: React.KeyboardEvent) => {
      e.stopPropagation(); // Esc belongs to the rename box, not the fullscreen overlay
      if (e.key === 'Enter') {
        e.preventDefault();
        commitEdit();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        setEditing(null);
      }
    },
    [commitEdit],
  );

  // ── toolbar actions ─────────────────────────────────────────────────────────
  const scrollToToday = useCallback(() => {
    const el = scrollRef.current;
    if (el && view.todayX !== null) el.scrollLeft = Math.max(0, view.todayX - el.clientWidth / 2);
  }, [view.todayX]);
  const fit = useCallback(() => {
    // Pick a zoom by the natural content span (not the fill-stretched width) so it stays readable.
    const span = view.rangeStart ? view.contentWidth / pxPerDay : 0;
    setZoom(span > 140 ? 'month' : span > 34 ? 'week' : 'day');
    if (scrollRef.current) scrollRef.current.scrollLeft = 0;
  }, [view.rangeStart, view.contentWidth, pxPerDay]);
  const resetRows = useCallback(() => {
    setRowHeights({});
    saveRowHeights(spec.title, {});
    setPanelWidth(GANTT_PANEL_DEFAULT);
    savePanelWidth(GANTT_PANEL_DEFAULT);
  }, [spec.title]);
  const customized =
    Object.keys(rowHeights).length > 0 || panelWidth !== GANTT_PANEL_DEFAULT;

  const toolbar = (
    <div className="gantt-toolbar" data-testid="gantt-toolbar">
      <div className="gantt-zoom" role="group" aria-label="Zoom">
        {ZOOMS.map((z) => (
          <button
            key={z}
            type="button"
            className="gantt-zoom__btn"
            data-active={zoom === z}
            onClick={() => setZoom(z)}
          >
            {z[0].toUpperCase() + z.slice(1)}
          </button>
        ))}
      </div>
      <button type="button" className="gantt-tbtn" onClick={scrollToToday} disabled={view.todayX === null} title="Scroll to today">
        Today
      </button>
      <button type="button" className="gantt-tbtn" onClick={fit} title="Fit to view">
        Fit
      </button>
      <button
        type="button"
        className="gantt-tbtn"
        data-active={showDeps}
        onClick={() => setShowDeps((v) => !v)}
        title="Toggle dependencies"
      >
        Deps
      </button>
      {customized && (
        <button
          type="button"
          className="gantt-tbtn"
          onClick={resetRows}
          title="Reset the task-list width and row heights"
        >
          Reset rows
        </button>
      )}
      {editable && (
        <span className="gantt-hint">Click a bar to edit details · double-click a name to rename · drag to reschedule</span>
      )}
    </div>
  );

  // Title + status legend — always shown (a deck slide needs a caption and a decodable key; the
  // server requires spec.title). Sits above the toolbar; survives into presentation mode.
  const header = (
    <div className="gantt-header">
      {spec.title && <div className="gantt-title">{spec.title}</div>}
      {(legend.statuses.size > 0 || legend.hasMilestone) && (
        <div className="gantt-legend" aria-label="Legend">
          {LEGEND_ORDER.filter((s) => legend.statuses.has(s)).map((s) => (
            <span key={s} className="gantt-legend__item">
              <span
                className="gantt-legend__swatch"
                style={{ background: STATUS_FILL[s], borderColor: STATUS_STROKE[s] }}
              />
              {STATUS_LABEL[s]}
            </span>
          ))}
          {legend.hasMilestone && (
            <span className="gantt-legend__item">
              <span
                className="gantt-legend__diamond"
                style={{ background: MILESTONE_FILL, borderColor: MILESTONE_STROKE }}
              />
              Milestone
            </span>
          )}
        </div>
      )}
    </div>
  );

  const renameInput = (
    <input
      className="gantt-panel__input"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onKeyDown={onEditKey}
      onBlur={commitEdit}
      onPointerDown={(e) => e.stopPropagation()}
      aria-label="Row name"
      data-testid="gantt-rename-input"
    />
  );

  return (
    <div className={`gantt-canvas${presenting ? ' gantt-canvas--slide' : ''}`}>
      {header}
      {!presenting && toolbar}
      <div
        className={`gantt-body${
          panelDrag ? ' is-resizing is-resizing-col' : rowDrag ? ' is-resizing is-resizing-row' : ''
        }`}
      >
        {/* Task-list panel */}
        <div className="gantt-panel" style={{ width: labelWidth }}>
          <div className="gantt-panel__head" style={{ height: axisHeight }}>
            Task
          </div>
          {view.rows.map((r, i) =>
            r.kind === 'section' ? (
              <div
                key={`s${i}`}
                className={`gantt-panel__section${r.collapsed ? ' is-collapsed' : ''}`}
                style={{ height: r.height }}
              >
                <button
                  type="button"
                  className="gantt-section__toggle"
                  aria-expanded={!r.collapsed}
                  title={r.collapsed ? 'Expand section' : 'Collapse section'}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={() => toggleCollapsed(r.sectionIndex!)}
                >
                  <span className="gantt-section__chevron" aria-hidden>
                    ▸
                  </span>
                </button>
                {editing?.kind === 'section' && editing.index === r.sectionIndex ? (
                  renameInput
                ) : (
                  <span
                    className="gantt-panel__name"
                    title={editable ? `${r.name} (double-click to rename)` : r.name}
                    onDoubleClick={() => startEdit({ kind: 'section', index: r.sectionIndex! }, r.name ?? '')}
                  >
                    {r.name}
                  </span>
                )}
                {r.collapsed && r.taskCount ? (
                  <span className="gantt-section__count" title={`${r.taskCount} tasks hidden`}>
                    {r.taskCount}
                  </span>
                ) : null}
                {editable && (
                  <button
                    type="button"
                    className="gantt-row__del"
                    disabled={(spec.sections[r.sectionIndex!]?.tasks.length ?? 0) > 0}
                    title={
                      (spec.sections[r.sectionIndex!]?.tasks.length ?? 0) > 0
                        ? 'Delete this section’s tasks first'
                        : 'Delete section'
                    }
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => commit(deleteSection(spec, r.sectionIndex!))}
                  >
                    ✕
                  </button>
                )}
              </div>
            ) : (
              <div
                key={r.id}
                className={`gantt-panel__task${r.id === selectedId ? ' is-selected' : ''}`}
                style={{ height: r.height }}
              >
                {editing?.kind === 'task' && editing.id === r.id ? (
                  renameInput
                ) : (
                  <>
                    <span
                      className="gantt-panel__name"
                      title={editable ? `${r.title} (click to inspect · double-click to rename)` : r.title}
                      onClick={() => editable && setSelectedId(r.id!)}
                      onDoubleClick={() => startEdit({ kind: 'task', id: r.id! }, r.title ?? '')}
                    >
                      {r.title}
                    </span>
                    {r.owner && <span className="gantt-panel__owner">{r.owner}</span>}
                  </>
                )}
                {editable && (
                  <button
                    type="button"
                    className="gantt-row__del"
                    title={`Delete “${r.title}”`}
                    data-testid="gantt-row-delete"
                    onPointerDown={(e) => e.stopPropagation()}
                    onClick={() => commit(deleteTask(spec, r.id!))}
                  >
                    ✕
                  </button>
                )}
                {!presenting && (
                  <div
                    className="gantt-row__grip"
                    role="separator"
                    aria-label={`Resize row ${r.title}`}
                    title="Drag to change row height"
                    onPointerDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setRowDrag({ id: r.id!, startY: e.clientY, startH: r.height });
                    }}
                  />
                )}
              </div>
            ),
          )}
        </div>

        {/* Panel / timeline divider */}
        {!presenting && (
          <div
            className="gantt-divider"
            role="separator"
            aria-orientation="vertical"
            aria-label="Resize task list"
            title="Drag to resize the task list"
            data-testid="gantt-panel-divider"
            onPointerDown={(e) => {
              e.preventDefault();
              setPanelDrag({ startX: e.clientX, startW: panelWidth });
            }}
            onDoubleClick={() => {
              setPanelWidth(GANTT_PANEL_DEFAULT);
              savePanelWidth(GANTT_PANEL_DEFAULT);
            }}
          />
        )}

        {/* Timeline */}
        <div className="gantt-timeline" ref={scrollRef}>
          {view.hasBars ? (
            <svg
              width={timelineWidth}
              height={canvasHeight}
              role="img"
              aria-label="Gantt timeline"
              onPointerMove={editable ? onPointerMove : undefined}
              onPointerUp={editable ? onPointerUp : undefined}
            >
              {/* weekend shading (extends full canvas height to fill the viewport) */}
              {view.weekends.map((w, i) => (
                <rect key={`we${i}`} x={w.x} y={axisHeight} width={w.width} height={canvasHeight - axisHeight} className="gantt-weekend" />
              ))}
              {/* grid: vertical at ticks */}
              {view.ticks.map((t, i) => (
                <line key={`g${i}`} x1={t.x} y1={axisHeight} x2={t.x} y2={canvasHeight} className={t.strong ? 'gantt-grid gantt-grid--strong' : 'gantt-grid'} />
              ))}
              {/* row separators + hover hit areas */}
              {view.rows.map((r, i) =>
                r.kind === 'task' ? (
                  <rect key={`row${i}`} x={0} y={r.y} width={timelineWidth} height={r.height} className="gantt-row-hit" />
                ) : (
                  <rect key={`row${i}`} x={0} y={r.y} width={timelineWidth} height={r.height} className="gantt-section-row" />
                ),
              )}
              {/* today line */}
              {view.todayX !== null && <line x1={view.todayX} y1={axisHeight - 6} x2={view.todayX} y2={canvasHeight} className="gantt-today" />}

              {/* two-tier axis */}
              <g className="gantt-axis">
                {view.upper.map((b, i) => (
                  <g key={`u${i}`}>
                    <line x1={b.x} y1={0} x2={b.x} y2={axisHeight} className="gantt-axis-div" />
                    {b.width > 54 && (
                      <text x={b.x + 6} y={16} className="gantt-axis-upper">
                        {b.label}
                      </text>
                    )}
                  </g>
                ))}
                {view.ticks.map((t, i) => (
                  <text key={`tl${i}`} x={t.x + 3} y={axisHeight - 8} className={t.strong ? 'gantt-axis-tick gantt-axis-tick--strong' : 'gantt-axis-tick'}>
                    {t.label}
                  </text>
                ))}
                <line x1={0} y1={axisHeight} x2={timelineWidth} y2={axisHeight} className="gantt-axis-base" />
              </g>

              {/* dependency elbows */}
              {showDeps &&
                view.deps.map((d, i) => {
                  const stub = 8;
                  const path = `M${d.x1},${d.y1} h${stub} V${d.y2} H${d.x2}`;
                  return (
                    <g key={`dep${i}`}>
                      <path d={path} className="gantt-dep" />
                      <path d={`M${d.x2 - 5},${d.y2 - 3} L${d.x2},${d.y2} L${d.x2 - 5},${d.y2 + 3} z`} className="gantt-dep-arrow" />
                    </g>
                  );
                })}

              {/* bars */}
              {view.rows.map((r) => {
                if (r.kind !== 'task' || !r.bar) return null;
                const b = r.bar;
                const active = drag?.id === b.id ? drag : null;
                const shift = active ? active.deltaDays * pxPerDay : 0;
                const offset = active?.mode === 'move' || active?.mode === 'resize-start' ? shift : 0;
                const grow = active?.mode === 'resize-end' ? shift : active?.mode === 'resize-start' ? -shift : 0;
                const x = b.x + offset;
                const w = Math.max(3, b.width + grow);
                const moveable = moveableIds.has(b.id);
                const cursor = editable && (moveable || b.milestone) ? 'grab' : 'default';
                const isSelected = b.id === selectedId;
                if (b.milestone) {
                  const cy = b.y + b.height / 2;
                  const rr = b.height / 2 + 1;
                  return (
                    <g key={b.id} className={isSelected ? 'gantt-ms is-selected' : 'gantt-ms'}>
                      {isSelected && (
                        <path
                          d={`M${x},${cy - rr - 3} l${rr + 3},${rr + 3} l${-rr - 3},${rr + 3} l${-rr - 3},${-rr - 3} z`}
                          className="gantt-select-ring"
                        />
                      )}
                      <path
                        d={`M${x},${cy - rr} l${rr},${rr} l${-rr},${rr} l${-rr},${-rr} z`}
                        fill={MILESTONE_FILL}
                        stroke={MILESTONE_STROKE}
                        strokeWidth={1.5}
                        style={{ cursor, touchAction: 'none' }}
                        onPointerDown={(e) => onBarDown(e, b.id, 'move')}
                      >
                        <title>{`${b.title} — ${b.startDate}`}</title>
                      </path>
                    </g>
                  );
                }
                return (
                  <g
                    key={b.id}
                    className={`gantt-bar${active ? ' is-dragging' : ''}${isSelected ? ' is-selected' : ''}`}
                  >
                    {isSelected && (
                      <rect x={x - 2} y={b.y - 2} width={w + 4} height={b.height + 4} rx={5} className="gantt-select-ring" />
                    )}
                    <rect x={x} y={b.y} width={w} height={b.height} rx={4} fill={STATUS_FILL[b.status]} stroke={STATUS_STROKE[b.status]} strokeWidth={1.25} />
                    {b.percent > 0 && (
                      <rect x={x} y={b.y} width={Math.max(2, w * b.percent)} height={b.height} rx={4} fill={STATUS_STROKE[b.status]} fillOpacity={0.32} />
                    )}
                    {/* transparent hit target on top for the drag (so the fill doesn't swallow pointer edge) */}
                    <rect
                      x={x}
                      y={b.y}
                      width={w}
                      height={b.height}
                      rx={4}
                      fill="transparent"
                      style={{ cursor, touchAction: 'none' }}
                      onPointerDown={(e) => onBarDown(e, b.id, 'move')}
                    >
                      <title>
                        {`${b.title}${b.owner ? ` · ${b.owner}` : ''} — ${b.startDate} → ${b.endDate}` +
                          (editable ? ' (drag to move · drag an end to resize)' : '')}
                      </title>
                    </rect>
                    {/* end handles — resize the bar from either side */}
                    {editable && (
                      <>
                        {moveable && (
                          <rect
                            x={x}
                            y={b.y}
                            width={Math.min(RESIZE_ZONE, w / 2)}
                            height={b.height}
                            fill="transparent"
                            className="gantt-bar__handle"
                            style={{ cursor: 'ew-resize', touchAction: 'none' }}
                            onPointerDown={(e) => onBarDown(e, b.id, 'resize-start')}
                          >
                            <title>Drag to change the start date</title>
                          </rect>
                        )}
                        <rect
                          x={x + w - Math.min(RESIZE_ZONE, w / 2)}
                          y={b.y}
                          width={Math.min(RESIZE_ZONE, w / 2)}
                          height={b.height}
                          fill="transparent"
                          className="gantt-bar__handle"
                          style={{ cursor: 'ew-resize', touchAction: 'none' }}
                          onPointerDown={(e) => onBarDown(e, b.id, 'resize-end')}
                        >
                          <title>Drag to change the duration</title>
                        </rect>
                      </>
                    )}
                  </g>
                );
              })}
            </svg>
          ) : (
            <div className="gantt-canvas--empty">No dated tasks to chart yet — give a task a start date or an `after`.</div>
          )}
        </div>

        {/* Right-docked metadata inspector — opens on selecting a bar/milestone */}
        {editable && !presenting && selectedTask && (
          <GanttInspector
            task={selectedTask}
            onPatch={patchSelected}
            onClose={() => setSelectedId(null)}
          />
        )}
      </div>
    </div>
  );
}
